/**
 * Authentication service for standalone login (outside WordPress).
 * Handles credential validation and session persistence.
 */

const SESSION_KEY = 'monkee_transcript_auth'

export interface AuthData {
  userId: number
  tennantId: string
  email: string
  displayName: string
}

interface LoginResponse {
  code: string
  data: AuthData
}

/**
 * Authenticates user against the backend, which validates credentials
 * against the WordPress installation.
 */
export async function login(email: string, password: string): Promise<AuthData> {
  const aiBaseUrl = (
    import.meta.env.VITE_AI_API_BASE_URL as string || ''
  ).replace(/\/$/, '')

  const apiKey = import.meta.env.VITE_API_KEY as string || ''

  if (!aiBaseUrl) {
    throw new Error('API URL not configured')
  }

  const response = await fetch(`${aiBaseUrl}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': apiKey
    },
    body: JSON.stringify({ email, password })
  })

  const text = await response.text()
  let data: any = null

  try {
    data = text ? JSON.parse(text) : null
  } catch {
    throw new Error('Invalid response from server')
  }

  if (!response.ok) {
    const msg = data?.msg || data?.message || data?.data?.msg || 'Authentication failed'
    throw new Error(msg)
  }

  // Backend may return HTTP 200 with error codes in body (via GlobalExceptionHandler)
  if (data && data.code && data.code !== 'OK' && data.code !== 'SUCCESS') {
    const msg = data.msg || data.message || 'Authentication failed'
    throw new Error(msg)
  }

  // The backend wraps in ApiResponse: { code: "OK", data: { userId, tennantId, ... } }
  const authData: AuthData = data?.data ?? data

  if (!authData?.userId || !authData?.tennantId) {
    throw new Error('Invalid authentication response')
  }

  // Persist in sessionStorage
  saveSession(authData)

  return authData
}

/**
 * Saves auth data to sessionStorage for persistence across page reloads.
 */
export function saveSession(authData: AuthData): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(authData))
  } catch {
    // sessionStorage not available (e.g., incognito mode limits)
    console.warn('Unable to persist auth session')
  }
}

/**
 * Retrieves auth data from sessionStorage.
 * Returns null if no session exists.
 */
export function getSession(): AuthData | null {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (!stored) return null
    const data = JSON.parse(stored) as AuthData
    if (data?.userId && data?.tennantId) {
      return data
    }
    return null
  } catch {
    return null
  }
}

/**
 * Clears the stored auth session (logout).
 */
export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // ignore
  }
}

/**
 * Populates window.monkeeTranscription with auth data from login.
 * This makes the rest of the app work seamlessly with the existing
 * auth header generation mechanism.
 */
export function applyAuthToConfig(authData: AuthData): void {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string || ''
  const aiBaseUrl = import.meta.env.VITE_AI_API_BASE_URL as string || ''
  const apiKey = import.meta.env.VITE_API_KEY as string || ''
  const secret = import.meta.env.VITE_API_SECRET as string || ''

  window.monkeeTranscription = {
    apiBaseUrl: baseUrl,
    aiBaseUrl: aiBaseUrl || 'http://localhost:8080/ai',
    apiKey: apiKey,
    encryptedKey: secret,
    userEmail: authData.email,
    userId: authData.userId,
    tennantId: authData.tennantId,
    ajaxUrl: '',
    restUrl: baseUrl,
    nonce: ''
  }
}

/**
 * Checks whether the app needs standalone login.
 * Returns true if:
 * - Not inside WordPress (no window.monkeeTranscription from WP)
 * - No test env variables configured (VITE_API_TEST_TENNANT / VITE_API_TEST_USER)
 */
export function needsLogin(): boolean {
  // Already has WordPress context
  if (window.monkeeTranscription?.userId && window.monkeeTranscription?.tennantId) {
    return false
  }

  // Has test env vars configured
  const testUser = import.meta.env.VITE_API_TEST_USER as string | undefined
  const testTennant = import.meta.env.VITE_API_TEST_TENNANT as string | undefined
  if (testUser && testTennant) {
    return false
  }

  return true
}
